import { ComputerUseNotePushMappingChip } from '@/components/tiptap/shared/computer-use-note-push-mapping-chip';
import { useComputerUseNotePushMappingContext } from '@/components/tiptap/shared/computer-use-note-push-mapping-context.helpers';
import { LinkEhrSectionDropdown } from '@/components/tiptap/shared/link-ehr-section-dropdown';
import { useSectionEhrContext } from '@/components/tiptap/shared/section-ehr-context';
import { cn } from '@/lib/cn';
import { createResizeObserverWithRAF } from '@/utils/resizeObserverWithRAF';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { DRAFT_SECTION_SENTINEL, getSectionDraftState } from '@heidi/editor/core';
import { type NodeViewProps } from '@tiptap/react';
import { Lock } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

/**
 * The EHR affordances that overlay a section heading: the "link this section to
 * an EHR field" dropdown and the computer-use note-push mapping chip, plus the
 * lock hint shown while a link freezes the heading text.
 *
 * Shared by both section node views (vMarkdown and markdown-native) so the
 * link/chip visibility rules, the heading key derivation and the heading-width
 * measurement have exactly one implementation. The node view keeps ownership of
 * everything else in its overlay (copy button, section actions) and of its own
 * label strings, matching how `SectionCopyButton` / `SectionActionsDropdown`
 * are already wired.
 *
 * Renders `null` when the surface has no EHR context, so a caller can mount it
 * unconditionally.
 */
export function SectionEhrToolbar({
  node,
  isEditable,
  isToolbarVisible,
  linkEhrAriaLabel,
  onLinkEhrOpenChange,
  onMappingOpenChange,
  updateAttributes,
}: {
  node: NodeViewProps['node'];
  /**
   * Unlinked "source" is omitted when the editor is read-only, because
   * `updateAttributes` writes even then. An already-linked field stays visible.
   */
  isEditable: boolean;
  /** Hover/focus visibility for the note-push mapping chip. The source link ignores this. */
  isToolbarVisible: boolean;
  linkEhrAriaLabel: string;
  /**
   * Popup-open reporters, handed straight to the two popups rather than mirrored
   * into local state first. The node view ORs them into its own visibility state
   * and into `useBlurEmptySection`, so a dropdown that steals focus can't be
   * mistaken for the user abandoning a draft — and that guard has to see the
   * open state in the SAME commit as the blur, which an effect-driven hop
   * through this component could not deliver.
   */
  onLinkEhrOpenChange: (open: boolean) => void;
  onMappingOpenChange: (open: boolean) => void;
  updateAttributes: NodeViewProps['updateAttributes'];
}) {
  const intl = useIntl();
  const { headingRawText, headingEmpty, isHeadingLocked, linkedEhrSectionId } =
    getSectionDraftState(node);

  const {
    integrationEhrProvider,
    canSyncSectionalNotes,
    availableStandardizedSections,
    onSectionLink,
  } = useSectionEhrContext();

  // Enabled only while an active note-push flow targets this template; the
  // default context is disabled (no chip) in standalone editors.
  const { enabled: isComputerUseNotePushMappingEnabled } =
    useComputerUseNotePushMappingContext();

  // Builders and SectionEhrProvider can read different providers, so suppress the link
  // chip while computer-use mapping is enabled — except an already-linked section (unlink lives here).
  const showLinkEhrSection =
    Boolean(onSectionLink) &&
    Boolean(integrationEhrProvider) &&
    canSyncSectionalNotes &&
    (Boolean(linkedEhrSectionId) ||
      (!isComputerUseNotePushMappingEnabled &&
        (availableStandardizedSections?.length ?? 0) > 0));
  const sectionHeadingKey = headingRawText.replace(DRAFT_SECTION_SENTINEL, '').trim();
  const showLinkedSource = showLinkEhrSection && isHeadingLocked;
  const showSource = showLinkedSource || (showLinkEhrSection && isEditable);
  const showSectionToolbar = showSource || isComputerUseNotePushMappingEnabled;

  const linkToolbarRef = useRef<HTMLDivElement>(null);
  const [h1RightOffset, setH1RightOffset] = useState<number | null>(null);

  useEffect(() => {
    const toolbar = linkToolbarRef.current;
    if (!toolbar) return;

    const section = toolbar.closest<HTMLElement>('section');
    if (!section) return;

    let ro: ResizeObserver | null = null;

    const update = () => {
      const currentH1 = section.querySelector<HTMLElement>('[data-node-view-content] h1');
      if (!currentH1) return;

      const sectionRect = section.getBoundingClientRect();
      const h1Rect = currentH1.getBoundingClientRect();
      const offset = h1Rect.right - sectionRect.left;

      if (offset > 0) {
        setH1RightOffset(offset);
      }
    };

    const attachToH1 = (): boolean => {
      const h1 = section.querySelector<HTMLElement>('[data-node-view-content] h1');
      if (!h1) return false;
      update();
      ro = createResizeObserverWithRAF(update);
      ro.observe(h1);
      return true;
    };

    if (!attachToH1()) {
      // h1 not yet injected by ProseMirror (e.g. remount after undo) — wait for it
      const mo = new MutationObserver(() => {
        if (attachToH1()) mo.disconnect();
      });
      mo.observe(section, { childList: true, subtree: true });
      return () => {
        mo.disconnect();
        ro?.disconnect();
      };
    }

    return () => ro?.disconnect();
  }, [showSectionToolbar]);

  if (!showSectionToolbar) return null;

  return (
    <div
      ref={linkToolbarRef}
      contentEditable={false}
      data-link-toolbar
      style={
        h1RightOffset != null
          ? ({ '--section-h1-width': `${h1RightOffset}px` } as React.CSSProperties)
          : undefined
      }
      className="absolute top-2 flex items-center gap-1"
    >
      {showLinkedSource && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex items-center text-text-tertiary">
              <Lock className="size-3" />
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {intl.formatMessage({
              defaultMessage: 'Remove the link to edit',
              id: 'SfwYRKC2Cp',
              description:
                'Tooltip on lock icon explaining how to unlock a linked section heading',
            })}
            <TooltipArrow />
          </TooltipContent>
        </Tooltip>
      )}
      {showSource && (
        <LinkEhrSectionDropdown
          onOpenChange={onLinkEhrOpenChange}
          ariaLabel={linkEhrAriaLabel}
          sections={availableStandardizedSections ?? []}
          linkedSectionId={linkedEhrSectionId}
          disabled={headingEmpty || !isEditable}
          onSelectSection={(sectionId) => {
            updateAttributes({ linkedEhrSectionId: sectionId });
            if (sectionHeadingKey) {
              onSectionLink?.(sectionHeadingKey, sectionId);
            }
          }}
        />
      )}
      {isComputerUseNotePushMappingEnabled && (
        <div
          data-note-push-mapping
          className={cn(
            'opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100',
            (isToolbarVisible || isHeadingLocked) && 'opacity-100'
          )}
        >
          <ComputerUseNotePushMappingChip
            heading={sectionHeadingKey}
            onOpenChange={onMappingOpenChange}
            disabled={headingEmpty}
          />
        </div>
      )}
    </div>
  );
}
